import styled from "styled-components";
import { useEffect, useState } from "react";
import { saveAs } from "file-saver";
import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import Formulario from "../../../Componentes/Utils/Formulario";
import FormComp from "../../../Componentes/FormComp";
import useApi from "../../../hooks/useApi";
import { formatBRLFromCents } from "../../../Funcoes/Money";
import { FiDownload } from "react-icons/fi";
import {
    AiOutlineFilePdf,
    AiOutlineFileImage,
    AiOutlineFileWord,
    AiOutlineFileExcel,
    AiOutlineFileUnknown
} from "react-icons/ai";

const SecaoTitulo = styled.p`
    margin: 18px 0 10px 0;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
`
const Container = styled.div`
    width: 100%;
    max-height: 320px;
    border: 1px solid rgba(0, 0, 0, 0.3);
    box-sizing: border-box;
    overflow-y: auto;
    border-radius: 8px;
`
const Lista = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`
const Linha = styled.li`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 13px;
    color: #333;
    &:nth-child(even) {
        background-color: #fafafa;
    }
    &:hover {
        background-color: #e0f7fa;
    }
`
const NomeArquivo = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`
const IconBtn = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    transition: .2s ease;
    &:hover {
        background: rgba(0, 0, 0, 0.06);
    }
`
const DownloadIco = styled(FiDownload)`
    height: 20px;
    width: 20px;
    color: #334155;
`
const Vazio = styled.p`
    margin: 0;
    padding: 24px;
    text-align: center;
    color: #64748b;
    font-size: 14px;
`

function normalizeAnexosPayload(data) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.anexos)) return data.anexos;
    return [];
}

function nomeAnexo(a) {
    const parts = resolveDownloadParts(a);
    return a.original_nome || a.nome || a.filename || (parts && parts.arquivo) || "Arquivo";
}

function resolveDownloadParts(anexo) {
    if (anexo.pasta && (anexo.arquivo || anexo.file)) {
        return { pasta: anexo.pasta, arquivo: anexo.arquivo || anexo.file };
    }
    const path = anexo.caminho || anexo.filepath || anexo.url || "";
    if (!path) return null;
    const segs = path.split("/").filter(Boolean);
    if (segs.length < 2) return null;
    return { pasta: segs[segs.length - 2], arquivo: segs[segs.length - 1] };
}

function getFileIcon(filename) {
    const name = String(filename || "").toLowerCase();
    if (name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg")) {
        return <AiOutlineFileImage size={22} color="#4a90e2" />;
    }
    if (name.endsWith(".pdf")) return <AiOutlineFilePdf size={22} color="#e63946" />;
    if (name.endsWith(".doc") || name.endsWith(".docx")) {
        return <AiOutlineFileWord size={22} color="#2a6db0" />;
    }
    if (name.endsWith(".xls") || name.endsWith(".xlsx")) {
        return <AiOutlineFileExcel size={22} color="#207245" />;
    }
    return <AiOutlineFileUnknown size={22} color="#555" />;
}

function pickValorNFCents(detalhes) {
    if (detalhes == null) return null;
    if (Array.isArray(detalhes) && detalhes.length > 0) {
        const sum = detalhes.reduce((acc, n) => {
            const raw = n.valorNF ?? n.valor_nf ?? n.valor ?? 0;
            const num = typeof raw === "number" ? raw : parseInt(String(raw), 10);
            return acc + (Number.isNaN(num) ? 0 : num);
        }, 0);
        return sum;
    }
    const keys = ["valor_nf", "valorNF", "valor_total_nf", "valorTotalNF"];
    for (const k of keys) {
        const v = detalhes[k];
        if (typeof v === "number" && !Number.isNaN(v)) return v;
        if (v != null && v !== "") {
            const n = parseInt(String(v), 10);
            if (!Number.isNaN(n)) return n;
        }
    }
    const nfs = detalhes.nfs;
    if (Array.isArray(nfs) && nfs.length > 0) {
        const sum = nfs.reduce((acc, n) => {
            const raw = n.valorNF ?? n.valor_nf ?? n.valor ?? 0;
            const num = typeof raw === "number" ? raw : parseInt(String(raw), 10);
            return acc + (Number.isNaN(num) ? 0 : num);
        }, 0);
        return sum;
    }
    return null;
}

const ModalVisualizarNF = ({ bm, fecharModal }) => {

    const api = useApi();

    const [ loading, setLoading ] = useState(true);
    const [ detalhes, setDetalhes ] = useState(null);
    const [ anexos, setAnexos ] = useState([]);

    useEffect(() => {

        const carregar = async () => {

            if (!bm?.id) {
                setLoading(false);
                return;
            }

            setLoading(true);

            try {

                const { data } = await api.get(
                    `/Painel/API/ControleMedicao/GetNFDetalhes/index.php?idBM=${bm.id}`
                );
                setDetalhes(data && typeof data === "object" ? data : {});
                setAnexos(normalizeAnexosPayload(data));

            } catch (e) {

                console.error(e);
                setDetalhes({});
                setAnexos([]);

            } finally {

                setLoading(false);

            }

        };

        carregar();

    }, [ bm?.id ]);

    const valorCents = pickValorNFCents(detalhes);
    const numMedicaoExibicao =
        detalhes?.num_medicao ?? detalhes?.numMedicao ?? bm?.num_medicao ?? "";

    const valorExibicao =
        valorCents != null ? formatBRLFromCents(valorCents) : "-";

    const preventSubmit = (e) => {
        e.preventDefault();
    };

    const handleDownload = async (anexo) => {

        try {

            if (anexo?.id != null) {
                const { data } = await api.get(
                    `/Painel/API/ControleMedicao/DownloadNFAnexo/index.php?id=${anexo.id}`,
                    { responseType: "blob" }
                );
                saveAs(data, nomeAnexo(anexo));
                return;
            }

            const parts = resolveDownloadParts(anexo);
            if (!parts) {
                console.warn("Anexo sem dados para download:", anexo);
                return;
            }

            const { data } = await api.get(
                `/Painel/API/DownloadFile/index.php?file=${encodeURIComponent(parts.arquivo)}&pasta=${encodeURIComponent(parts.pasta)}`,
                { responseType: "blob" }
            );
            saveAs(data, nomeAnexo(anexo));

        } catch (e) {

            console.error(e);

        }

    };

    return (
        <Overlay state={true}>
            <Modal header close={fecharModal}>
                <Formulario onSubmit={preventSubmit}>
                    <FormComp
                        title="Nº BM"
                        type="text"
                        readOnly
                        valor={numMedicaoExibicao !== "" ? String(numMedicaoExibicao) : "-"}
                    />
                    <FormComp
                        title="VALOR DA NF"
                        type="text"
                        readOnly
                        valor={loading ? "CARREGANDO..." : valorExibicao}
                    />
                    <SecaoTitulo>ANEXOS</SecaoTitulo>
                    <Container>
                        {loading ? (
                            <Vazio>CARREGANDO...</Vazio>
                        ) : anexos.length === 0 ? (
                            <Vazio>Nenhum anexo disponível.</Vazio>
                        ) : (
                            <Lista>
                                {anexos.map((a, idx) => (
                                    <Linha key={a.id != null ? a.id : `nf-anexo-${idx}`}>
                                        {getFileIcon(nomeAnexo(a))}
                                        <NomeArquivo title={nomeAnexo(a)}>{nomeAnexo(a)}</NomeArquivo>
                                        <IconBtn title="Baixar" onClick={() => handleDownload(a)}>
                                            <DownloadIco />
                                        </IconBtn>
                                    </Linha>
                                ))}
                            </Lista>
                        )}
                    </Container>
                </Formulario>
            </Modal>
        </Overlay>
    );
};

export default ModalVisualizarNF;
